import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import { useSubscription } from '../SubscriptionContext';
import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Heart, Users, Brain, Compass, FileText, CheckCircle, BarChart3, Info, Home, Save } from 'lucide-react';

const AffinityCompatibility = () => {
    const { currentUser } = useAuth();
    const { subscription, isPremium } = useSubscription();
    const navigate = useNavigate(); // ← FIX: Adăugat pentru redirectare
    const [currentView, setCurrentView] = useState('home');
    const [selectedTest, setSelectedTest] = useState(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [isLoaded, setIsLoaded] = useState(false); // ← FIX: Flag pentru protecție salvare

    // Load saved progress from Firestore on mount
    useEffect(() => {
        const loadFromFirestore = async () => {
            if (!currentUser) {
                setIsLoaded(true); // ← FIX: Marchează ca loaded chiar dacă nu e user
                return;
            }

            try {
                const docRef = doc(db, 'userResponses', currentUser.uid);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    console.log('✅ Date încărcate din Firestore:', data);

                    setAnswers(data.answers || {});
                    if (data.currentTest) setSelectedTest(data.currentTest);
                    if (data.currentQuestionIndex !== undefined) setCurrentQuestionIndex(data.currentQuestionIndex);
                    if (data.currentView) setCurrentView(data.currentView);
                } else {
                    console.log('📝 Document nou, creez structura inițială');
                    // Creează document gol dacă nu există
                    await setDoc(docRef, {
                        answers: {},
                        subscription: subscription || 'none'
                    });
                }
            } catch (error) {
                console.error('❌ Eroare la încărcarea datelor din Firestore:', error);
            } finally {
                setIsLoaded(true); // ← FIX: Setează flag DUPĂ load
            }
        };

        loadFromFirestore();
    }, [currentUser]); // Nu include subscription în dependencies!

    // Save all data to Firestore whenever relevant state changes
    useEffect(() => {
        // Debounce: salvează la 1.5 secunde după ultima modificare
        const timer = setTimeout(async () => {
            // Nu salva dacă nu e user sau datele nu sunt încărcate
            if (!currentUser || !isLoaded) {
                return;
            }

            // Nu salva answers dacă e gol (previne ștergerea)
            if (Object.keys(answers).length === 0 && !selectedTest) {
                return;
            }

            try {
                const docRef = doc(db, 'userResponses', currentUser.uid);

                // Citește subscription-ul existent pentru a-l păstra
                const docSnap = await getDoc(docRef);
                const existingSubscription = docSnap.exists()
                    ? docSnap.data().subscription
                    : (subscription || 'none');

                await setDoc(docRef, {
                    answers,
                    currentTest: selectedTest,
                    currentQuestionIndex,
                    currentView,
                    subscription: existingSubscription
                }, { merge: true });

            } catch (error) {
                console.error('❌ Eroare la salvarea în Firestore:', error);
            }
        }, 1500);

        return () => clearTimeout(timer);
    }, [answers, selectedTest, currentQuestionIndex, currentView, currentUser, isLoaded, subscription]);

    // NIVEL 1 - Dimensiunea 4: Stil de Viață
    const nivel1Dim4 = {
        id: 'n1d4',
        level: 1,
        title: "Stil de Viață",
        icon: Users,
        color: "blue",
        questions: [
            {
                id: "n1d4q1",
                text: "Perspectiva predominantă asupra banilor:",
                type: "single",
                options: [
                    { value: "economii", label: "Economiile reprezintă modul în care îmi valorizez banii" },
                    { value: "experiente", label: "Lucrurile pe care le cumpăr și experiențele pe care le am reprezintă modul în care îmi valorizez banii" }
                ]
            },
            {
                id: "n1d4q2",
                text: "Ce preferințe predominante ai pentru petrecerea timpului liber? (alege toate care se aplică)",
                type: "multiple",
                options: [
                    { value: "calatorii", label: "Călătorii" },
                    { value: "iesiri_prieteni", label: "Ieșiri cu prietenii" },
                    { value: "plimbari_natura", label: "Plimbări în natură" },
                    { value: "experiente_noi", label: "Sunt deschis(ă) spre experiențe noi" },
                    { value: "acasa", label: "Petrecerea timpului acasă" },
                    { value: "activitati_solitare", label: "Prefer predominant activități solitare" },
                    { value: "socializare_limitata", label: "Socializare selectivă și limitată" }
                ]
            },
            {
                id: "n1d4q3",
                text: "Care este piatra de temelie pentru o relație romantică de succes?",
                type: "single",
                options: [
                    { value: "sex", label: "Sex" },
                    { value: "prietenie", label: "Prietenie" }
                ]
            },
            {
                id: "n1d4q4",
                text: "Sexualitate:",
                type: "single",
                options: [
                    { value: "traditional", label: "Nu am nevoie de jucării sau alte practici non-tradiționale pentru a fi împlinit(ă) sexual" },
                    { value: "explorativ", label: "Îmi place să condimentez lucrurile în pat și sunt deschis(ă) să încerc lucruri noi" }
                ]
            },
            {
                id: "n1d4q5",
                text: "Am nevoie să fiu într-o relație pentru: (alege toate care se aplică)",
                type: "multiple",
                options: [
                    { value: "companie", label: "Companie" },
                    { value: "sprijin_emotional", label: "Sprijin emoțional" },
                    { value: "apartenenta", label: "Sentimentul de apartenență" },
                    { value: "dezvoltare", label: "Dezvoltare și cunoaștere personală" },
                    { value: "sprijin_financiar", label: "Sprijin financiar" },
                    { value: "iubire", label: "Iubire" },
                    { value: "sex", label: "Sex" },
                    { value: "sa_nu_fiu_singur", label: "Să nu fiu singur(ă)" }
                ]
            }
        ]
    };

    // NIVEL 2 - Dimensiunea 4: Limbaje ale Iubirii
    const nivel2Dim4 = {
        id: 'n2d4',
        level: 2,
        title: "Limbaje ale Iubirii",
        icon: Heart,
        color: "pink",
        questions: [
            {
                id: "n2d4q1",
                text: "Prefer să petrec timp de calitate alături de partenerul meu",
                type: "likert",
                category: "timp_calitate"
            },
            {
                id: "n2d4q2",
                text: "Apreciez cadourile primite de la partenerul meu",
                type: "likert",
                category: "cadouri"
            },
            {
                id: "n2d4q3",
                text: "Îmi place să aud cuvinte de apreciere și iubire din partea partenerului meu",
                type: "likert",
                category: "cuvinte_afirmare"
            },
            {
                id: "n2d4q4",
                text: "Simt o conexiune mai profundă prin îmbrățișări și atingeri fizice",
                type: "likert",
                category: "contact_fizic"
            },
            {
                id: "n2d4q5",
                text: "Consider că gesturile de ajutor și suport sunt importante în relație",
                type: "likert",
                category: "acte_serviciu"
            }
        ]
    };

    // NIVEL 2 - Dimensiunea 5: Inteligență Emoțională
    const nivel2Dim5 = {
        id: 'n2d5',
        level: 2,
        title: "Inteligență Emoțională",
        icon: Brain,
        color: "purple",
        questions: [
            {
                id: "n2d5q1",
                text: "Ce ai învățat din relațiile trecute?",
                type: "single",
                options: [
                    { value: "a", label: "Că nu trebuie să mă mai încred în oameni", score: 1 },
                    { value: "b", label: "Că nu sunt suficient(ă) și trebuie să fiu mai bun(ă)", score: 2 },
                    { value: "c", label: "Că am tipare repetitive pe care încerc să le înțeleg", score: 3 },
                    { value: "d", label: "Că fiecare relație a fost o lecție valoroasă despre mine", score: 4 }
                ]
            },
            {
                id: "n2d5q2",
                text: "Când cineva apropiat îți spune că l-ai rănit, cum reacționezi?",
                type: "single",
                options: [
                    { value: "a", label: "Mă apăr și îi arăt că și el greșește", score: 1 },
                    { value: "b", label: "Mă simt vinovat(ă) și mă retrag", score: 2 },
                    { value: "c", label: "Încerc să înțeleg ce s-a întâmplat și discut", score: 3 },
                    { value: "d", label: "Îmi asum responsabilitatea, cer feedback și repar", score: 4 }
                ]
            },
            {
                id: "n2d5q3",
                text: "Cât de ușor îți este să spui: \"Am nevoie de tine\"?",
                type: "single",
                options: [
                    { value: "a", label: "Deloc – asta mă face slab(ă)", score: 1 },
                    { value: "b", label: "Foarte greu – îmi e frică de respingere", score: 2 },
                    { value: "c", label: "Pot spune, dar doar în contexte sigure", score: 3 },
                    { value: "d", label: "Mă simt confortabil exprimând nevoi emoționale", score: 4 }
                ]
            },
            {
                id: "n2d5q4",
                text: "Ce faci când cineva se apropie emoțional de tine?",
                type: "single",
                options: [
                    { value: "a", label: "Îl resping sau mă îndepărtez", score: 1 },
                    { value: "b", label: "Mă simt sufocat(ă) și confuz(ă)", score: 2 },
                    { value: "c", label: "Încerc să mă reglez și să observ ce simt", score: 3 },
                    { value: "d", label: "Mă bucur și rămân deschis(ă)", score: 4 }
                ]
            },
            {
                id: "n2d5q5",
                text: "Cum reacționezi în timpul unui conflict?",
                type: "single",
                options: [
                    { value: "a", label: "Ridic tonul sau plec", score: 1 },
                    { value: "b", label: "Mă închid în mine și evit discuția", score: 2 },
                    { value: "c", label: "Cer o pauză și revin ulterior", score: 3 },
                    { value: "d", label: "Rămân prezent(ă) și exprim emoțiile asertiv", score: 4 }
                ]
            },
            {
                id: "n2d5q6",
                text: "Cum îți exprimi limitele?",
                type: "single",
                options: [
                    { value: "a", label: "Nu prea le exprim – mi-e frică de respingere", score: 1 },
                    { value: "b", label: "Le impun dur sau după ce explodez", score: 2 },
                    { value: "c", label: "Încerc să le exprim calm, dar nu reușesc mereu", score: 3 },
                    { value: "d", label: "Le comunic clar, respectuos și constant", score: 4 }
                ]
            },
            {
                id: "n2d5q7",
                text: "Ce înseamnă pentru tine intimitatea emoțională?",
                type: "single",
                options: [
                    { value: "a", label: "O slăbiciune periculoasă", score: 1 },
                    { value: "b", label: "O nevoie pe care mi-e greu să o exprim", score: 2 },
                    { value: "c", label: "Un spațiu în care pot să cresc, dar mă tem uneori", score: 3 },
                    { value: "d", label: "O conexiune autentică în care pot fi eu însumi/însămi", score: 4 }
                ]
            },
            {
                id: "n2d5q8",
                text: "Ce faci când simți că partenerul nu te iubește temporar?",
                type: "single",
                options: [
                    { value: "a", label: "Devin distant(ă) sau caut validare în altă parte", score: 1 },
                    { value: "b", label: "Intru în anxietate sau îl confrunt agresiv", score: 2 },
                    { value: "c", label: "Îmi exprim temerea și cer clarificare", score: 3 },
                    { value: "d", label: "Mă reglez emoțional și comunic cu răbdare", score: 4 }
                ]
            },
            {
                id: "n2d5q9",
                text: "Ce înseamnă o relație matură pentru tine?",
                type: "single",
                options: [
                    { value: "a", label: "O relație în care nimeni nu suferă și totul merge bine", score: 1 },
                    { value: "b", label: "O relație în care partenerul mă face fericit(ă)", score: 2 },
                    { value: "c", label: "Un parteneriat bazat pe susținere și comunicare", score: 3 },
                    { value: "d", label: "Un spațiu de creștere, asumare și vindecare reciprocă", score: 4 }
                ]
            },
            {
                id: "n2d5q10",
                text: "Ce rol are o relație în viața ta?",
                type: "single",
                options: [
                    { value: "a", label: "Să mă împlinească și să-mi aline rănile", score: 1 },
                    { value: "b", label: "Să am pe cineva lângă mine să nu fiu singur(ă)", score: 2 },
                    { value: "c", label: "Să împărtășim viața și să ne însoțim în creștere", score: 3 },
                    { value: "d", label: "Să contribuim unul la evoluția celuilalt cu respect", score: 4 }
                ]
            }
        ]
    };

    // NIVEL 3 - Motivații Subconștiente
    const nivel3 = {
        id: 'nivel3',
        level: 3,
        title: "Motivații Subconștiente",
        icon: Brain,
        color: "indigo",
        questions: [
            {
                id: "n3q1",
                text: "Ce simți când cineva te iubește sincer, fără să ceară nimic în schimb?",
                type: "single",
                options: [
                    { value: "a", label: "Mă simt recunoscător(are), dar pare suspect" },
                    { value: "b", label: "Mi-e greu să cred, nu merit atâta iubire" },
                    { value: "c", label: "Mă simt sufocat(ă)" },
                    { value: "d", label: "E ceea ce mi-am dorit mereu" }
                ]
            },
            {
                id: "n3q2",
                text: "Ce te atrage cel mai mult la o persoană?",
                type: "single",
                options: [
                    { value: "a", label: "Independența și misterul" },
                    { value: "b", label: "Faptul că are nevoie de mine" },
                    { value: "c", label: "Că mă provoacă sau îmi atinge punctele sensibile" },
                    { value: "d", label: "Că îmi este familiară" }
                ]
            },
            {
                id: "n3q3",
                text: "Care dintre următoarele fraze ți se pare adevărată pentru tine?",
                type: "single",
                options: [
                    { value: "a", label: "Mă îndrăgostesc de persoane indisponibile" },
                    { value: "b", label: "Am tendința să salvez oameni" },
                    { value: "c", label: "Mă plictisesc dacă totul merge bine" },
                    { value: "d", label: "Am ales parteneri care mă fac să mă simt mic(ă) sau inadecvat(ă)" }
                ]
            },
            {
                id: "n3q4",
                text: "Ce tip de relație ți se pare iubire adevărată?",
                type: "single",
                options: [
                    { value: "a", label: "Intensă, pasională, cu suișuri și coborâșuri" },
                    { value: "b", label: "Calmă, sigură, predictibilă" },
                    { value: "c", label: "Una în care eu dau mai mult" },
                    { value: "d", label: "Una în care lupt pentru a fi iubit(ă)" }
                ]
            },
            {
                id: "n3q5",
                text: "Care dintre următoarele afirmații te reprezintă mai mult?",
                type: "single",
                options: [
                    { value: "a", label: "Când iubesc, îmi pierd granițele" },
                    { value: "b", label: "Îmi este greu să am încredere că cineva mă va alege pe termen lung" },
                    { value: "c", label: "Aleg mereu persoane care au nevoie să fie reparate" },
                    { value: "d", label: "Mă simt atras(ă) de oameni care mă intimidează" }
                ]
            },
            {
                id: "n3q6",
                text: "Cum te simți când ești respins(ă)?",
                type: "single",
                options: [
                    { value: "a", label: "Ca și cum s-ar confirma ceva ce deja știam" },
                    { value: "b", label: "Mă obsedează, vreau să recâștig persoana" },
                    { value: "c", label: "Mă simt rușinat(ă) și mă retrag" },
                    { value: "d", label: "Mă face să vreau și mai tare persoana respectivă" }
                ]
            },
            {
                id: "n3q7",
                text: "Ce gânduri ai despre relațiile părinților tăi sau despre iubirea primită în copilărie?",
                type: "single",
                options: [
                    { value: "a", label: "Era distantă sau impredictibilă" },
                    { value: "b", label: "Am simțit că trebuie să \"merit\" iubirea" },
                    { value: "c", label: "Mi-a lipsit complet apropierea" },
                    { value: "d", label: "Părinții mei aveau o relație tensionată, dar nu m-am implicat" }
                ]
            }
        ]
    };

    // NIVEL 4 - Personalitate MCMI - 16 categorii cu displayName prietenos
    const nivel4 = {
        id: 'nivel4',
        level: 4,
        title: "Trăsături de Personalitate (MCMI)",
        icon: Users,
        color: "emerald",
        categories: [
            {
                name: "Schizoid",
                displayName: "Solitar & Rezervat",
                questions: [
                    { id: "n4_schizoid_1", text: "Mă simt confortabil să petrec timp singur(ă) și nu am nevoie permanent de compania altora" },
                    { id: "n4_schizoid_2", text: "Nu simt nevoia să împărtășesc tot ce gândesc sau simt cu partenerul meu" },
                    { id: "n4_schizoid_3", text: "Prețuiesc spațiul personal și timpul doar pentru mine" }
                ]
            },
            {
                name: "Masochist",
                displayName: "Altruist & Dedicat",
                questions: [
                    { id: "n4_masochist_1", text: "Pun nevoile celorlalți înaintea propriilor mele nevoi, chiar dacă îmi este greu" },
                    { id: "n4_masochist_2", text: "Mi-e greu să refuz ajutorul atunci când cineva îmi cere, chiar dacă nu am timp sau energie" },
                    { id: "n4_masochist_3", text: "Rămân lângă oameni și în situații dificile, chiar dacă îmi provoacă suferință" },
                    { id: "n4_masochist_4", text: "Mă simt valoros(valoroasă) când fac sacrificii pentru cei dragi" }
                ]
            },
            {
                name: "Dependent",
                displayName: "Orientat spre Conexiune",
                questions: [
                    { id: "n4_dependent_1", text: "Mă simt mai în siguranță când cineva apropiat mă sprijină în deciziile mele" },
                    { id: "n4_dependent_2", text: "Am nevoie să știu că persoana iubită este mereu disponibilă pentru mine" },
                    { id: "n4_dependent_3", text: "Mi-e dificil să fiu singur pentru perioade lungi de timp și evit singurătatea" }
                ]
            },
            {
                name: "Narcisist",
                displayName: "Încrezător & Ambițios",
                questions: [
                    { id: "n4_narcisist_1", text: "Îmi place să fiu centrul atenției în grup și să fiu remarcat pentru ceea ce fac" },
                    { id: "n4_narcisist_2", text: "Îmi place să fiu un punct de referință pentru cei din jur" },
                    { id: "n4_narcisist_3", text: "Cred că merit tot ce este mai bun în viață" }
                ]
            },
            {
                name: "Compulsiv",
                displayName: "Organizat & Perfecționist",
                questions: [
                    { id: "n4_compulsiv_1", text: "Îmi place ca totul să fie organizat și planificat înainte, pentru a evita surprizele" },
                    { id: "n4_compulsiv_2", text: "Mă simt bine doar dacă în casă este totul organizat, ordonat și curat" },
                    { id: "n4_compulsiv_3", text: "Îmi place să am control asupra detaliilor mici din viața mea și să fiu sigur că totul este în ordine" }
                ]
            },
            {
                name: "Evitant",
                displayName: "Precaut & Sensibil",
                questions: [
                    { id: "n4_evitant_1", text: "Îmi este greu să am încredere în ceilalți, chiar dacă aș vrea să fiu mai deschis și mai conectat" },
                    { id: "n4_evitant_2", text: "Îmi este dificil să vorbesc despre sentimentele mele, ceilalți mă pot judeca sau respinge" },
                    { id: "n4_evitant_3", text: "Mă retrag adesea din relații pentru a evita riscul de a fi rănit(ă) sau respins(ă)" }
                ]
            },
            {
                name: "Sadic",
                displayName: "Dominant & Asertiv",
                questions: [
                    { id: "n4_sadic_1", text: "Îmi place să am controlul într-o relație" },
                    { id: "n4_sadic_2", text: "Spun direct ce cred, chiar dacă poate fi incomod pentru ceilalți" },
                    { id: "n4_sadic_3", text: "Îmi place să am putere și să pot decide pentru ceilalți, indiferent de preferințele ori părerile lor" },
                    { id: "n4_sadic_4", text: "Îmi place să provoc și să testez limitele celorlalți pentru a vedea până unde pot merge" }
                ]
            },
            {
                name: "Histrionic",
                displayName: "Expresiv & Sociabil",
                questions: [
                    { id: "n4_histrionic_1", text: "Îmi place să fiu în centrul atenției și să primesc admirație din partea celor din jur" },
                    { id: "n4_histrionic_2", text: "Îmi plac situațiile în care pot interacționa cu mulți oameni" },
                    { id: "n4_histrionic_3", text: "Am tendința să spun povești sau să fac glume pentru ca ceilalți să mă placă" }
                ]
            },
            {
                name: "Antisocial",
                displayName: "Nonconformist & Aventurier",
                questions: [
                    { id: "n4_antisocial_1", text: "Uneori nu respect regulile și legile dacă consider că asta mă ajută să obțin ceea ce vreau" },
                    { id: "n4_antisocial_2", text: "Îmi place să risc și uneori mă implic în activități periculoase fără să mă gândesc prea mult la consecințe" },
                    { id: "n4_antisocial_3", text: "Nu simt vinovăție atunci când fac lucruri care rănesc sau deranjează pe alții" }
                ]
            },
            {
                name: "Psihopat",
                displayName: "Strategic & Detașat",
                questions: [
                    { id: "n4_psihopat_1", text: "Pot manipula situațiile și oamenii cu ușurință pentru a obține ceea ce vreau" },
                    { id: "n4_psihopat_2", text: "Rareori simt remușcări sau vinovăție pentru acțiunile mele, chiar dacă alții sunt afectați" },
                    { id: "n4_psihopat_3", text: "Sunt foarte bun(ă) la a-mi ascunde adevăratele intenții și emoții față de ceilalți" }
                ]
            },
            {
                name: "Negativist",
                displayName: "Sceptic & Critic",
                questions: [
                    { id: "n4_negativist_1", text: "Mă simt adesea sceptic față de intențiile celorlalți și cred că, în general, oamenii nu spun adevărul" },
                    { id: "n4_negativist_2", text: "Mă enervează când mi se spune ce să fac, chiar dacă are dreptate" },
                    { id: "n4_negativist_3", text: "În general, observ mai repede defectele situațiilor sau ale celorlalți și mai puțin punctele lor forte" },
                    { id: "n4_negativist_4", text: "În situații stresante, reacționez adesea cu reproșuri, mai degrabă decât cu soluții" }
                ]
            },
            {
                name: "Schizotipal",
                displayName: "Neconvențional & Creativ",
                questions: [
                    { id: "n4_schizotipal_1", text: "Tind să am convingeri sau idei care diferă de cele ale celor din jurul meu și par ciudate pentru alții" },
                    { id: "n4_schizotipal_2", text: "Am gânduri sau experiențe care sunt neobișnuite și dificil de explicat pentru ceilalți" }
                ]
            },
            {
                name: "Borderline",
                displayName: "Intens & Pasional",
                questions: [
                    { id: "n4_borderline_1", text: "Oscilez între a idealiza și devaloriza persoanele din jurul meu, în zile sau momente diferite" },
                    { id: "n4_borderline_2", text: "Într-o zi pot trece de la entuziasm la tristețe fără un motiv clar" },
                    { id: "n4_borderline_3", text: "Îmi este dificil să-mi controlez furia sau frustrările și pot reacționa foarte intens în situații de conflict" },
                    { id: "n4_borderline_4", text: "După o ceartă sau un conflict, pot simți o frică intensă de a fi abandonat(ă)" }
                ]
            },
            {
                name: "Paranoid",
                displayName: "Vigilent & Precaut",
                questions: [
                    { id: "n4_paranoid_1", text: "Mă simt adesea suspicios/suspicioasă față de intențiile sau sinceritatea celor din jur" },
                    { id: "n4_paranoid_2", text: "Sunt adesea neliniștit(ă) sau îngrijorat(ă) de motivele și adevăratele intenții ale celorlalți" }
                ]
            },
            {
                name: "Anxios",
                displayName: "Sensibil & Prevăzător",
                questions: [
                    { id: "n4_anxios_1", text: "Trec ușor de la stări de calm la stări de neliniște sau panică fără un motiv clar" },
                    { id: "n4_anxios_2", text: "Îmi fac multe scenarii înainte de un eveniment important" },
                    { id: "n4_anxios_3", text: "În situații sociale, mă simt adesea nesigur și temător, chiar dacă vreau să fiu mai relaxat" },
                    { id: "n4_anxios_4", text: "Uneori mă neliniștesc chiar și pentru detalii minore" }
                ]
            },
            {
                name: "Depresiv",
                displayName: "Introspectiv & Melancolic",
                questions: [
                    { id: "n4_depresiv_1", text: "Mă simt adesea trist(ă) chiar și atunci când nu există motive clare" },
                    { id: "n4_depresiv_2", text: "Pierd interesul pentru activitățile care odată îmi aduceau plăcere" },
                    { id: "n4_depresiv_3", text: "Mă simt adesea demoralizat(ă) și am dificultăți în a avea încredere în viitor" },
                    { id: "n4_depresiv_4", text: "Mă simt adesea obosit sau fără motivație, chiar dacă am dormit suficient" }
                ]
            }
        ]
    };

    // Define all test modules
    const testModules = {
        nivel1_dim1: {
            id: 'nivel1_dim1',
            level: 1,
            title: "Statut Economic-Social",
            icon: Users,
            color: "blue",
            questions: [
                {
                    id: "n1d1q1",
                    text: "Ce rol îți dorești ca partenerul să aibă în relație, din punct de vedere financiar?",
                    type: "single",
                    options: [
                        { value: "a", label: "Să contribuim egal – vreau o relație partenerială" },
                        { value: "b", label: "Să contribuie mai mult decât mine, dar nu exclusiv" },
                        { value: "c", label: "Să câștige mai puțin, dar să fie echilibrat(ă) și practic(ă)" },
                        { value: "d", label: "Să fie principalul susținător financiar al familiei" },
                        { value: "e", label: "Nu contează – să aibă grijă de casă/familie și să mă sprijine emoțional" },
                        { value: "f", label: "Fiecare să fie responsabil financiar pentru sine" }
                    ]
                },
                {
                    id: "n1d1q2",
                    text: "Ce nivel de educație ți se pare potrivit pentru partenerul tău?",
                    type: "single",
                    options: [
                        { value: "a", label: "Cel puțin egal cu al meu" },
                        { value: "b", label: "Poate fi mai jos, dacă este deschis la învățare și are bun simț" },
                        { value: "c", label: "Mai înalt decât al meu – admir persoanele intelectuale" },
                        { value: "d", label: "Nu contează educația formală, contează inteligența emoțională" }
                    ]
                },
                {
                    id: "n1d1q3",
                    text: "Cât de important este pentru tine ca el/ea să aibă prestigiu social?",
                    type: "single",
                    options: [
                        { value: "a", label: "Foarte important – îmi crește admirația și încrederea" },
                        { value: "b", label: "Important, dar nu esențial" },
                        { value: "c", label: "Puțin important – contează mai mult caracterul" },
                        { value: "d", label: "Nu are nicio importanță" }
                    ]
                },
                {
                    id: "n1d1q4",
                    text: "Care din următoarele scenarii ți se pare cel mai potrivit?",
                    type: "single",
                    options: [
                        { value: "a", label: "Un partener(a) prosper(a), care îmi oferă siguranță și stabilitate" },
                        { value: "b", label: "Un partener(a) echilibrat(a), cu un venit mediu și o atitudine responsabilă" },
                        { value: "c", label: "Un partener(a) simplu, dar cu multă afecțiune și prezență" },
                        { value: "d", label: "Un partener(a) vizionar, chiar dacă instabil acum" }
                    ]
                },
                {
                    id: "n1d1q5",
                    text: "Ai accepta un partener care:",
                    type: "single",
                    options: [
                        { value: "a", label: "Încă luptă să se ridice, dar are visuri mari" },
                        { value: "b", label: "Prefer un partener deja realizat, cu stabilitate dovedită" },
                        { value: "c", label: "Are o viață modestă și preferă o viață simplă" }
                    ]
                }
            ]
        },
        nivel1_dim2: {
            id: 'nivel1_dim2',
            level: 1,
            title: "Independență vs. Nevoia de Sprijin",
            icon: Users,
            color: "blue",
            questions: [
                {
                    id: "n1d2q1",
                    text: "Cum te simți într-o relație în care fiecare are viața lui și vă vedeți seara sau în weekend?",
                    type: "single",
                    options: [
                        { value: "a", label: "Perfect – am nevoie de spațiu și autonomie" },
                        { value: "b", label: "Îmi convine atâta timp cât ne reconectăm des" },
                        { value: "c", label: "Nu-mi place – simt că nu suntem cu adevărat 'împreună'" },
                        { value: "d", label: "Mă simt neglijat(ă) și nesigur(ă)" }
                    ]
                },
                {
                    id: "n1d2q2",
                    text: "Ce fel de sprijin îți dorești de la partener(ă)?",
                    type: "single",
                    options: [
                        { value: "a", label: "Mai degrabă emoțional și în decizii importante" },
                        { value: "b", label: "Un sprijin constant, inclusiv practic și material" },
                        { value: "c", label: "Libertate totală, dar să știu că este acolo când contează" },
                        { value: "d", label: "Aștept prezență, implicare și atenție constantă" }
                    ]
                },
                {
                    id: "n1d2q3",
                    text: "Dacă partenerul tău ar trece printr-o perioadă dificilă financiar sau emoțional, ce ai face?",
                    type: "single",
                    options: [
                        { value: "a", label: "Îl/o sprijin moral, dar nu îmi pot asuma tot" },
                        { value: "b", label: "Mă implic total – suntem o echipă" },
                        { value: "c", label: "Îl/o susțin, dar îmi pun limite clare" },
                        { value: "d", label: "Aș simți că e o povară dacă durează mult" }
                    ]
                }
            ]
        },
        nivel1_dim3: {
            id: 'nivel1_dim3',
            level: 1,
            title: "Roluri de Gen & Leadership",
            icon: Users,
            color: "blue",
            questions: [
                {
                    id: "n1d3q1",
                    text: "Cum vezi rolurile bărbatului și femeii într-o relație?",
                    type: "single",
                    options: [
                        { value: "a", label: "Egale, fiecare contribuie cum poate și cum vrea" },
                        { value: "b", label: "Tradiționale – bărbatul susține, femeia creează cămin" },
                        { value: "c", label: "Mix – roluri adaptate contextului și personalității" },
                        { value: "d", label: "Femeia să se simtă în siguranță, bărbatul să conducă" }
                    ]
                },
                {
                    id: "n1d3q2",
                    text: "Cine ar trebui să ia deciziile majore în cuplu?",
                    type: "single",
                    options: [
                        { value: "a", label: "Împreună, cu dialog și compromis" },
                        { value: "b", label: "Cel mai rațional și informat dintre noi" },
                        { value: "c", label: "Bărbatul – ca lider natural" },
                        { value: "d", label: "Femeia – dacă are viziune clară" }
                    ]
                },
                {
                    id: "n1d3q3",
                    text: "Cum reacționezi când partenerul(ta) îți spune ce să faci?",
                    type: "single",
                    options: [
                        { value: "a", label: "Dacă are dreptate, sunt deschis(ă)" },
                        { value: "b", label: "Nu îmi place, dar accept uneori" },
                        { value: "c", label: "Mă deranjează – am nevoie de autonomie" },
                        { value: "d", label: "Mă simt controlat(ă) – reacționez negativ" }
                    ]
                },
                {
                    id: "n1d3q4",
                    text: "Ce tip de autoritate ți se pare atrăgătoare într-o relație?",
                    type: "single",
                    options: [
                        { value: "a", label: "Soft – influență prin înțelepciune și calm" },
                        { value: "b", label: "Fermitate clară – să știu cine conduce" },
                        { value: "c", label: "Deloc – relația nu are nevoie de autoritate" },
                        { value: "d", label: "Alternativă – fiecare conduce în ce se pricepe" }
                    ]
                }
            ]
        },
        nivel1_dim4: nivel1Dim4,
        nivel2_dim1: {
            id: 'nivel2_dim1',
            level: 2,
            title: "Stil de Atașament",
            icon: Heart,
            color: "purple",
            questions: [
                {
                    id: "n2d1q1",
                    text: "Cum reacționezi când partenerul se distanțează emoțional?",
                    type: "single",
                    options: [
                        { value: "a", label: "Mă simt anxios(ă), încerc să îl/o apropii cu orice preț", score: { anxious: 1 } },
                        { value: "b", label: "Îmi pierd interesul și mă retrag", score: { avoidant: 1 } },
                        { value: "c", label: "Îmi exprim neliniștea, dar respect spațiul celuilalt", score: { secure: 1 } },
                        { value: "d", label: "Mă simt confuz(ă), uneori insist, alteori fug", score: { disorganized: 1 } }
                    ]
                },
                {
                    id: "n2d1q2",
                    text: "Cum îți exprimi iubirea într-o relație?",
                    type: "single",
                    options: [
                        { value: "a", label: "Am nevoie să fiu mereu prezent(ă), să știu ce face", score: { anxious: 1 } },
                        { value: "b", label: "Prefer intimitatea limitată, exprim cu dificultate sentimentele", score: { avoidant: 1 } },
                        { value: "c", label: "Sunt sincer(ă) și afectuos(ă), dar echilibrat(ă)", score: { secure: 1 } },
                        { value: "d", label: "Oscilez între a oferi mult și a mă închide brusc", score: { disorganized: 1 } }
                    ]
                },
                {
                    id: "n2d1q3",
                    text: "Ce simți când partenerul îți cere apropiere?",
                    type: "single",
                    options: [
                        { value: "a", label: "Mi-e teamă că nu sunt suficient(ă), dar mă apropii oricum", score: { anxious: 1 } },
                        { value: "b", label: "Mă simt sufocat(ă) și vreau spațiu", score: { avoidant: 1 } },
                        { value: "c", label: "Mă bucur, îmi place intimitatea autentică", score: { secure: 1 } },
                        { value: "d", label: "Mă apropii, dar apoi îmi vine să fug", score: { disorganized: 1 } }
                    ]
                },
                {
                    id: "n2d1q4",
                    text: "Cum reacționezi la conflictele din cuplu?",
                    type: "single",
                    options: [
                        { value: "a", label: "Devin neliniștit(ă), nu pot să stau fără să rezolv imediat", score: { anxious: 1 } },
                        { value: "b", label: "Prefer să evit conflictul, mă detașez", score: { avoidant: 1 } },
                        { value: "c", label: "Discut deschis, dar cu empatie", score: { secure: 1 } },
                        { value: "d", label: "Devin imprevizibil(ă), reacționez haotic", score: { disorganized: 1 } }
                    ]
                },
                {
                    id: "n2d1q5",
                    text: "Ce crezi despre tine în relații?",
                    type: "single",
                    options: [
                        { value: "a", label: "Simt că nu sunt suficient(ă) sau voi fi părăsit(ă)", score: { anxious: 1 } },
                        { value: "b", label: "Mă simt mai bine singur(ă) decât vulnerabil(ă)", score: { avoidant: 1 } },
                        { value: "c", label: "Am încredere în mine și în partener", score: { secure: 1 } },
                        { value: "d", label: "Simt că e ceva greșit cu mine, dar nu știu ce", score: { disorganized: 1 } }
                    ]
                }
            ]
        },
        nivel2_dim2: {
            id: 'nivel2_dim2',
            level: 2,
            title: "Valori Relaționale",
            icon: Compass,
            color: "cyan",
            questions: [
                {
                    id: "n2d2q1",
                    text: "Ce apreciezi cel mai mult într-o relație?",
                    type: "single",
                    options: [
                        { value: "stabilitate", label: "Siguranța, predictibilitatea" },
                        { value: "libertate", label: "Libertatea de exprimare și mișcare" },
                        { value: "evolutie", label: "Să evoluăm împreună, să învățăm" },
                        { value: "aventura", label: "Aventură, spontaneitate, noutate" },
                        { value: "loialitate", label: "Fidelitate, încredere profundă" },
                        { value: "spiritualitate", label: "Conexiune profundă spirituală" }
                    ]
                },
                {
                    id: "n2d2q2",
                    text: "Ce te-ar face să te retragi dintr-o relație?",
                    type: "single",
                    options: [
                        { value: "stabilitate", label: "Nesiguranța sau instabilitatea" },
                        { value: "libertate", label: "Să fiu controlat(ă) sau limitat(ă)" },
                        { value: "evolutie", label: "Lipsa unui scop comun sau a progresului" },
                        { value: "aventura", label: "Monotonia și rutina" },
                        { value: "loialitate", label: "Trădarea încrederii" },
                        { value: "spiritualitate", label: "Superficialitatea conexiunii" }
                    ]
                },
                {
                    id: "n2d2q3",
                    text: "Care este genul tău ideal de partener?",
                    type: "single",
                    options: [
                        { value: "stabilitate", label: "Stabil, calm, prezent" },
                        { value: "libertate", label: "Independent, cu granițe clare" },
                        { value: "evolutie", label: "Deschis la dezvoltare personală" },
                        { value: "aventura", label: "Energic, curios, dispus să încerce orice" },
                        { value: "loialitate", label: "Devotat, loial, protector" },
                        { value: "spiritualitate", label: "Introspectiv, conectat la dimensiunea divină" }
                    ]
                },
                {
                    id: "n2d2q4",
                    text: "Cum îți imaginezi un weekend ideal în cuplu?",
                    type: "single",
                    options: [
                        { value: "stabilitate", label: "Relaxare acasă, siguranță și liniște" },
                        { value: "libertate", label: "Fiecare cu hobby-ul lui, spațiu personal" },
                        { value: "evolutie", label: "Workshop sau retreat de dezvoltare" },
                        { value: "aventura", label: "Excursie spontană într-un loc necunoscut" },
                        { value: "loialitate", label: "Timp împreună, fără distrageri, încredere deplină" },
                        { value: "spiritualitate", label: "Meditație, ritualuri, discuții spirituale" }
                    ]
                },
                {
                    id: "n2d2q5",
                    text: "Ce te face să te simți iubit(ă)?",
                    type: "single",
                    options: [
                        { value: "stabilitate", label: "Când știu că mă pot baza pe celălalt" },
                        { value: "libertate", label: "Când nu trebuie să renunț la mine pentru relație" },
                        { value: "evolutie", label: "Când mă încurajează să mă dezvolt" },
                        { value: "aventura", label: "Când râdem, explorăm și trăim intens" },
                        { value: "loialitate", label: "Când îmi este loial în toate circumstanțele" },
                        { value: "spiritualitate", label: "Când simt că ne conectăm dincolo de cuvinte" }
                    ]
                }
            ]
        },
        nivel2_dim3: {
            id: 'nivel2_dim3',
            level: 2,
            title: "Maturitate Emoțională",
            icon: Brain,
            color: "green",
            questions: [
                {
                    id: "n2d3q1",
                    text: "Cum reacționezi când te simți respins(ă)?",
                    type: "single",
                    options: [
                        { value: "a", label: "Mă retrag sau devin defensiv(ă)", score: 1 },
                        { value: "b", label: "Încerc să înțeleg ce a dus la reacția celuilalt", score: 3 },
                        { value: "c", label: "Mă supăr și cer explicații agresiv", score: 0 },
                        { value: "d", label: "Îmi iau timp să reflectez și comunic cu calm", score: 4 }
                    ]
                },
                {
                    id: "n2d3q2",
                    text: "Cum îți exprimi emoțiile negative?",
                    type: "single",
                    options: [
                        { value: "a", label: "Le reprim, prefer să nu le arăt", score: 2 },
                        { value: "b", label: "Explodez și apoi regret", score: 1 },
                        { value: "c", label: "Le exprim în mod asertiv și conștient", score: 4 },
                        { value: "d", label: "Depinde, uneori le exprim prea puternic, alteori le ignor", score: 2 }
                    ]
                },
                {
                    id: "n2d3q3",
                    text: "Când partenerul tău are o zi proastă, cum reacționezi?",
                    type: "single",
                    options: [
                        { value: "a", label: "Mă supăr că nu îmi oferă atenție", score: 1 },
                        { value: "b", label: "Îl/o susțin fără a mă pierde pe mine", score: 4 },
                        { value: "c", label: "Mă simt responsabil(ă) să-l/o repar", score: 2 },
                        { value: "d", label: "Îl/o evit, ca să nu mă încarc", score: 1 }
                    ]
                },
                {
                    id: "n2d3q4",
                    text: "Ce faci când ai un conflict într-o relație?",
                    type: "single",
                    options: [
                        { value: "a", label: "Evit discuția sau mă închid în mine", score: 1 },
                        { value: "b", label: "Țip sau atac, ca să mă apăr", score: 0 },
                        { value: "c", label: "Încerc să înțeleg perspectiva celuilalt și exprim nevoile mele", score: 4 },
                        { value: "d", label: "Îi dau dreptate, ca să păstrez liniștea", score: 2 }
                    ]
                },
                {
                    id: "n2d3q5",
                    text: "Ce înseamnă 'asumarea responsabilității emoționale' pentru tine?",
                    type: "single",
                    options: [
                        { value: "a", label: "Să nu-mi arăt slăbiciunile", score: 1 },
                        { value: "b", label: "Să-mi asum ce simt, fără a da vina pe ceilalți", score: 4 },
                        { value: "c", label: "Să nu cer nimic de la ceilalți", score: 2 },
                        { value: "d", label: "Să rezolv totul singur(ă)", score: 2 }
                    ]
                },
                {
                    id: "n2d3q6",
                    text: "Cum îți gestionezi nevoile și limitele personale?",
                    type: "single",
                    options: [
                        { value: "a", label: "Nu prea știu care sunt", score: 1 },
                        { value: "b", label: "Le impun fără compromisuri", score: 2 },
                        { value: "c", label: "Le exprim clar, dar cu empatie", score: 4 },
                        { value: "d", label: "Mi-e teamă să le exprim", score: 2 }
                    ]
                }
            ]
        },
        nivel2_dim4: nivel2Dim4,
        nivel2_dim5: nivel2Dim5,
        nivel3: nivel3,
        nivel4: nivel4
    };

    const handleAnswer = (questionId, value, type = 'single') => {
        setAnswers(prev => {
            const newAnswers = { ...prev };

            if (type === 'multiple') {
                if (!newAnswers[questionId]) {
                    newAnswers[questionId] = [];
                }
                if (newAnswers[questionId].includes(value)) {
                    newAnswers[questionId] = newAnswers[questionId].filter(v => v !== value);
                } else {
                    newAnswers[questionId] = [...newAnswers[questionId], value];
                }
            } else {
                newAnswers[questionId] = value;
            }

            return newAnswers;
        });
    };

    const getCurrentTest = () => testModules[selectedTest];

    const getCurrentQuestion = () => {
        const test = getCurrentTest();
        if (test?.id === 'nivel4') {
            const allQuestions = test.categories.flatMap(cat => cat.questions);
            return allQuestions[currentQuestionIndex];
        }
        return test?.questions[currentQuestionIndex];
    };

    const getTotalQuestions = () => {
        const test = getCurrentTest();
        if (!test) return 0;
        if (test.id === 'nivel4') {
            return test.categories.reduce((sum, cat) => sum + cat.questions.length, 0);
        }
        return test.questions.length;
    };

    const renderLikertScale = (questionId) => {
        const value = answers[questionId] || 3;
        return (
            <div className="flex items-center justify-between gap-2 max-w-md mx-auto">
                {[1, 2, 3, 4, 5].map((num) => (
                    <button
                        key={num}
                        onClick={() => handleAnswer(questionId, num, 'likert')}
                        className={`flex-1 py-3 rounded-lg border-2 transition-all font-semibold ${value === num
                            ? 'border-blue-600 bg-blue-600 text-white'
                            : 'border-slate-300 bg-white text-slate-700 hover:border-blue-300'
                            }`}
                    >
                        {num}
                    </button>
                ))}
            </div>
        );
    };

    const renderQuestion = () => {
        const question = getCurrentQuestion();
        const test = getCurrentTest();

        if (!question) return null;

        if (test.id === 'nivel4') {
            const category = test.categories.find(cat => cat.questions.includes(question));
            return (
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                    <div className="mb-6">
                        <div className="text-sm font-medium text-blue-600 mb-2">
                            {category?.displayName || category?.name}
                        </div>
                        <h3 className="text-xl font-semibold text-slate-900 leading-relaxed">
                            {question.text}
                        </h3>
                    </div>
                    <div className="mb-6">
                        <div className="flex justify-between text-xs text-slate-600 mb-2 max-w-md mx-auto">
                            <span>Deloc adevărat</span>
                            <span>Foarte adevărat</span>
                        </div>
                        {renderLikertScale(question.id)}
                    </div>
                </div>
            );
        }

        if (question.type === 'likert') {
            return (
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                    <h3 className="text-xl font-semibold text-slate-900 mb-6 leading-relaxed">
                        {question.text}
                    </h3>
                    <div className="mb-6">
                        <div className="flex justify-between text-xs text-slate-600 mb-2 max-w-md mx-auto">
                            <span>Deloc adevărat</span>
                            <span>Foarte adevărat</span>
                        </div>
                        {renderLikertScale(question.id)}
                    </div>
                </div>
            );
        }

        const currentAnswer = answers[question.id];
        const isMultiple = question.type === 'multiple';

        return (
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                <h3 className="text-xl font-semibold text-slate-900 mb-6 leading-relaxed">
                    {question.text}
                </h3>

                {isMultiple && (
                    <div className="mb-4 text-sm text-slate-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
                        <Info size={16} className="inline mr-2" />
                        Poți selecta mai multe opțiuni
                    </div>
                )}

                <div className="space-y-3">
                    {question.options.map((option) => {
                        const isSelected = isMultiple
                            ? (currentAnswer || []).includes(option.value)
                            : currentAnswer === option.value;

                        return (
                            <label
                                key={option.value}
                                className={`flex items-start p-4 rounded-xl border-2 cursor-pointer transition-all ${isSelected
                                    ? 'border-blue-600 bg-blue-50'
                                    : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                                    }`}
                            >
                                <input
                                    type={isMultiple ? 'checkbox' : 'radio'}
                                    name={question.id}
                                    value={option.value}
                                    checked={isSelected}
                                    onChange={() => handleAnswer(question.id, option.value, question.type)}
                                    className="mt-0.5 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="ml-3 text-slate-700 leading-relaxed flex-1">
                                    {option.label}
                                </span>
                            </label>
                        );
                    })}
                </div>
            </div>
        );
    };

    const isQuestionAnswered = () => {
        const question = getCurrentQuestion();
        if (!question) return false;
        const answer = answers[question.id];
        if (question.type === 'multiple') {
            return answer && answer.length > 0;
        }
        return answer !== undefined && answer !== null;
    };

    const handleNext = () => {
        const total = getTotalQuestions();
        if (currentQuestionIndex < total - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const startTest = (testId) => {
        setSelectedTest(testId);
        setCurrentQuestionIndex(0);
        setCurrentView('test');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // ← FIX: Redirectează la ProfilePage tab "Rezultate"
    const goToProfile = () => {
        navigate('/profile?tab=results');
    };

    const goToHome = () => {
        setCurrentView('home');
        setSelectedTest(null);
        setCurrentQuestionIndex(0);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const resetProgress = () => {
        if (window.confirm('Ești sigur că vrei să resetezi tot progresul? Această acțiune nu poate fi anulată.')) {
            setAnswers({});
            setSelectedTest(null);
            setCurrentQuestionIndex(0);
            setCurrentView('home');
        }
    };

    const renderHome = () => {
        if (subscription === 'none') {
            return <Navigate to="/pricing" replace />;
        }
        const testCategories = {
            'Nivel 1: Standarde Extrinseci': [
                { id: 'nivel1_dim1', title: 'Statut Economic-Social', icon: Users, desc: '5 întrebări despre roluri financiare și educație' },
                { id: 'nivel1_dim2', title: 'Independență vs. Sprijin', icon: Users, desc: '3 întrebări despre autonomie și nevoi de suport' },
                { id: 'nivel1_dim3', title: 'Roluri de Gen & Leadership', icon: Users, desc: '4 întrebări despre dinamica cuplului' },
                { id: 'nivel1_dim4', title: 'Stil de Viață', icon: Users, desc: '5 întrebări despre valori și preferințe' }
            ],
            'Nivel 2: Standarde Intrinseci': [
                { id: 'nivel2_dim1', title: 'Stil de Atașament', icon: Heart, desc: '5 întrebări despre răspunsuri emoționale' },
                { id: 'nivel2_dim2', title: 'Valori Relaționale', icon: Compass, desc: '5 întrebări despre prioritățile în relație' },
                { id: 'nivel2_dim3', title: 'Maturitate Emoțională', icon: Brain, desc: '6 întrebări despre reglare emoțională' },
                { id: 'nivel2_dim4', title: 'Limbaje ale Iubirii', icon: Heart, desc: '5 întrebări pe scală Likert' },
                { id: 'nivel2_dim5', title: 'Inteligență Emoțională', icon: Brain, desc: '10 întrebări despre conștientizare' }
            ],
            'Nivel 3: Motivații Subconștiente': [
                { id: 'nivel3', title: 'Tipare Subconștiente', icon: Brain, desc: '7 întrebări despre rănile și tiparele emoționale' }
            ],
            'Nivel 4: Trăsături de Personalitate': [
                { id: 'nivel4', title: 'MCMI - Personalitate', icon: Users, desc: '55 întrebări pe scală Likert despre 16 dimensiuni' }
            ]
        };

        // Testele disponibile pentru abonamentul Standard (Nivel 1 + Nivel 2)
        const standardTests = [
            'nivel1_dim1', 'nivel1_dim2', 'nivel1_dim3', 'nivel1_dim4',
            'nivel2_dim1', 'nivel2_dim2', 'nivel2_dim3', 'nivel2_dim4', 'nivel2_dim5'
        ];

        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-12 px-6">
                <div className="max-w-6xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-600 rounded-full mb-6">
                            <FileText className="text-white" size={40} />
                        </div>
                        <h1 className="text-4xl font-bold text-slate-900 mb-4">
                            Evaluare de Compatibilitate Affinity
                        </h1>
                        <p className="text-xl text-slate-600 mb-4">
                            Test Psihologic Complet - 4 Niveluri Progressive
                        </p>

                        {currentUser && (
                            <div className="mt-2 inline-block px-3 py-1 rounded-full text-sm font-semibold bg-purple-100 text-purple-700">
                                {isPremium ? '🌟 Premium' : '⭐ Standard'}
                            </div>
                        )}

                        {Object.keys(answers).length > 0 && (
                            <div className="inline-flex items-center gap-4 bg-green-50 border border-green-200 rounded-full px-6 py-3 mt-4">
                                <Save className="text-green-600" size={20} />
                                <span className="text-green-700 font-medium">
                                    Progres salvat: {Object.keys(answers).length} răspunsuri
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Test Categories */}
                    {Object.entries(testCategories).map(([category, tests]) => (
                        <div key={category} className="mb-10">
                            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
                                <div className="w-2 h-8 bg-blue-600 rounded mr-4"></div>
                                {category}
                            </h2>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {tests.map(test => {
                                    const Icon = test.icon;
                                    const testData = testModules[test.id];
                                    const totalQuestions = test.id === 'nivel4'
                                        ? testData.categories.reduce((sum, cat) => sum + cat.questions.length, 0)
                                        : testData.questions.length;

                                    const answeredQuestions = testData.questions
                                        ? testData.questions.filter(q => answers[q.id]).length
                                        : test.id === 'nivel4'
                                            ? testData.categories.flatMap(cat => cat.questions).filter(q => answers[q.id]).length
                                            : 0;

                                    const isComplete = answeredQuestions === totalQuestions;
                                    const isInProgress = answeredQuestions > 0 && !isComplete;
                                    const isAccessible = (subscription === 'premium') ||
                                        (subscription === 'standard' && standardTests.includes(test.id));

                                    const handleCardClick = () => {
                                        // Verifică dacă testul e complet
                                        if (isComplete) {
                                            if (window.confirm(
                                                '⚠️ Ai completat deja acest test!\n\n' +
                                                'Pentru a-l reface, trebuie să resetezi toate testele din Profilul tău (tab Rezultate).\n\n' +
                                                'Vrei să mergi la Profil acum?'
                                            )) {
                                                navigate('/profile?tab=results');
                                            }
                                            return; // ← BLOCHEAZĂ accesul la test
                                        }

                                        if (isAccessible) {
                                            startTest(test.id);
                                        } else {
                                            alert('Acest test este disponibil doar în abonamentul Premium. Fă upgrade pentru a accesa!');
                                        }
                                    };

                                    return (
                                        <div
                                            key={test.id}
                                            onClick={handleCardClick}
                                            className={`bg-white rounded-2xl shadow-lg border border-slate-200 p-6 transition-all ${isComplete
                                                ? 'opacity-70 cursor-not-allowed border-green-300' // ← Blocat dacă complet
                                                : isAccessible
                                                    ? 'cursor-pointer hover:shadow-xl hover:scale-105' // ← Accesibil
                                                    : 'opacity-60 cursor-not-allowed' // ← Premium locked
                                                }`}
                                        >
                                            <div className="flex items-start justify-between mb-4">
                                                <div className={`w-12 h-12 ${!isAccessible
                                                    ? 'bg-gray-300'
                                                    : isComplete
                                                        ? 'bg-green-100'
                                                        : isInProgress
                                                            ? 'bg-yellow-100'
                                                            : 'bg-blue-100'
                                                    } rounded-xl flex items-center justify-center`}>
                                                    {!isAccessible ? (
                                                        <span className="text-gray-600 font-bold text-sm">🔒</span>
                                                    ) : isComplete ? (
                                                        <CheckCircle className="text-green-600" size={24} />
                                                    ) : (
                                                        <Icon className={isInProgress ? 'text-yellow-600' : 'text-blue-600'} size={24} />
                                                    )}
                                                </div>
                                                {isComplete && (
                                                    <span className="bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full">
                                                        Completat
                                                    </span>
                                                )}
                                                {isInProgress && (
                                                    <span className="bg-yellow-100 text-yellow-700 text-xs font-semibold px-3 py-1 rounded-full">
                                                        În progres
                                                    </span>
                                                )}
                                                {!isAccessible && (
                                                    <span className="bg-purple-100 text-purple-700 text-xs font-semibold px-3 py-1 rounded-full">
                                                        Premium
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="text-lg font-bold text-slate-900 mb-2">
                                                {test.title}
                                            </h3>
                                            <p className="text-sm text-slate-600 mb-4">
                                                {test.desc}
                                            </p>
                                            {isAccessible && (
                                                <div className="flex items-center justify-between text-xs text-slate-500">
                                                    <span>{answeredQuestions}/{totalQuestions} răspunsuri</span>
                                                    {answeredQuestions > 0 && (
                                                        <div className="w-24 bg-slate-200 rounded-full h-1.5">
                                                            <div
                                                                className={`h-1.5 rounded-full ${isComplete ? 'bg-green-600' : 'bg-yellow-500'
                                                                    }`}
                                                                style={{ width: `${(answeredQuestions / totalQuestions) * 100}%` }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
                        {Object.keys(answers).length > 0 && (
                            <>
                                <button
                                    onClick={goToProfile}
                                    className="bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-700 transition-colors shadow-lg flex items-center justify-center"
                                >
                                    <BarChart3 size={20} className="mr-2" />
                                    Vezi Profilul Meu
                                </button>

                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderTest = () => {
        const test = getCurrentTest();
        if (!test) return null;

        const totalQuestions = getTotalQuestions();
        const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;
        const Icon = test.icon;
        const isCompleted = currentQuestionIndex === totalQuestions - 1 && isQuestionAnswered();

        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
                {/* Header */}
                <div className="bg-white shadow-md sticky top-0 z-10 border-b border-slate-200">
                    <div className="max-w-4xl mx-auto px-6 py-5">
                        <div className="flex items-center justify-between mb-4">
                            <button
                                onClick={goToHome}
                                className="flex items-center text-slate-600 hover:text-slate-900 font-medium"
                            >
                                <Home size={20} className="mr-2" />
                                Acasă
                            </button>
                            <div className="text-right">
                                <div className="text-sm font-medium text-slate-600">Întrebarea</div>
                                <div className="text-xl font-bold text-blue-600">
                                    {currentQuestionIndex + 1} / {totalQuestions}
                                </div>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                                <div
                                    className="bg-blue-600 h-2 transition-all duration-500"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                            <div className="text-xs text-slate-600 mt-2 text-right">
                                {Math.round(progress)}% completat
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="max-w-4xl mx-auto px-6 py-8">
                    {/* Test Header */}
                    <div className="mb-8">
                        <div className="flex items-center mb-4">
                            <div className={`w-12 h-12 bg-${test.color}-600 rounded-xl flex items-center justify-center mr-4`}>
                                <Icon className="text-white" size={24} />
                            </div>
                            <div>
                                <div className="text-sm font-medium text-blue-600">Nivel {test.level}</div>
                                <h2 className="text-2xl font-bold text-slate-900">{test.title}</h2>
                            </div>
                        </div>
                    </div>

                    {/* Question */}
                    {renderQuestion()}

                    {/* Navigation */}
                    <div className="mt-8 flex justify-between items-center">
                        <button
                            onClick={handlePrevious}
                            disabled={currentQuestionIndex === 0}
                            className="flex items-center px-6 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-slate-300"
                        >
                            <ChevronLeft size={20} className="mr-2" />
                            Înapoi
                        </button>

                        {isCompleted ? (
                            <button
                                onClick={goToHome}
                                className="flex items-center px-8 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors shadow-lg"
                            >
                                <CheckCircle size={20} className="mr-2" />
                                Test Finalizat
                            </button>
                        ) : (
                            <button
                                onClick={handleNext}
                                disabled={!isQuestionAnswered()}
                                className="flex items-center px-8 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-slate-400"
                            >
                                Următoarea
                                <ChevronRight size={20} className="ml-2" />
                            </button>
                        )}
                    </div>

                    {!isQuestionAnswered() && (
                        <div className="mt-4 text-center text-sm text-amber-600 bg-amber-50 rounded-lg py-2 px-4 border border-amber-200">
                            Te rugăm să răspunzi la întrebare pentru a continua
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Main render
    if (currentView === 'home') return renderHome();
    if (currentView === 'test') return renderTest();
    return renderHome(); // Default fallback
};

export default AffinityCompatibility;